import { useEffect, useState, type ReactNode, type FormEvent } from 'react';
import { apiRequest } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';

interface IncidentRow {
  readonly incidentId: string;
  readonly incidentNumber: string;
  readonly title: string;
  readonly severity: string;
  readonly status: string;
  readonly ownerId: string | null;
}

export function M19IncidentDashboardPage(): ReactNode {
  const { t } = useTranslation();
  const { token } = useAuth();
  const [rows, setRows] = useState<IncidentRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);

  const [title, setTitle] = useState('');
  const [severity, setSeverity] = useState('MEDIUM');
  const [impact, setImpact] = useState('MEDIUM');
  const [description, setDescription] = useState('');
  const [creating, setCreating] = useState(false);
  const [created, setCreated] = useState(false);

  const load = (): void => {
    apiRequest<{ data: IncidentRow[] }>('/v1/platform/incidents', { token })
      .then((r) => setRows(r.data))
      .catch((e) => setError((e as Error).message));
  };

  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [token]);

  const onCreate = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    setCreating(true);
    setCreated(false);
    try {
      await apiRequest('/v1/platform/incidents', {
        method: 'POST', token,
        body: { title, severity, impact, description: description || undefined },
      });
      setCreated(true);
      setTitle('');
      setDescription('');
      load();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setCreating(false);
    }
  };

  const act = async (id: string, action: 'resolve' | 'close' | 'escalate'): Promise<void> => {
    setBusyId(id + action);
    try {
      const body =
        action === 'resolve' ? { resolution: 'Resolved from ops console' }
          : action === 'escalate' ? { reason: 'Escalated from ops console' }
            : undefined;
      await apiRequest(`/v1/platform/incidents/${id}/${action}`, { method: 'POST', token, body });
      load();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusyId(null);
    }
  };

  if (error && !rows) return <p role="alert">{error}</p>;
  if (!rows) return <p>{t('m19.incident.loading')}</p>;
  return (
    <main aria-label="incident-dashboard">
      <h1>{t('m19.incident.title')}</h1>

      <form onSubmit={onCreate} aria-label="incident-create-form">
        <h2>{t('m19.incident.logIncident')}</h2>
        <label htmlFor="inc-title">{t('m19.incident.labelTitle')}</label>
        <input id="inc-title" value={title} onChange={(e) => setTitle(e.target.value)} required />
        <label htmlFor="inc-severity">{t('m19.incident.labelSeverity')}</label>
        <select id="inc-severity" aria-label="incident-severity" value={severity} onChange={(e) => setSeverity(e.target.value)}>
          <option value="LOW">{t('m19.incident.optLow')}</option>
          <option value="MEDIUM">{t('m19.incident.optMedium')}</option>
          <option value="HIGH">{t('m19.incident.optHigh')}</option>
          <option value="CRITICAL">{t('m19.incident.optCritical')}</option>
        </select>
        <label htmlFor="inc-impact">{t('m19.incident.labelImpact')}</label>
        <select id="inc-impact" aria-label="incident-impact" value={impact} onChange={(e) => setImpact(e.target.value)}>
          <option value="LOW">{t('m19.incident.optLow')}</option>
          <option value="MEDIUM">{t('m19.incident.optMedium')}</option>
          <option value="HIGH">{t('m19.incident.optHigh')}</option>
        </select>
        <label htmlFor="inc-desc">{t('m19.incident.labelDescription')}</label>
        <textarea id="inc-desc" value={description} onChange={(e) => setDescription(e.target.value)} />
        {error ? <p role="alert">{error}</p> : null}
        {created ? <p role="status">{t('m19.incident.created')}</p> : null}
        <button type="submit" disabled={creating}>{creating ? t('m19.incident.logging') : t('m19.incident.logButton')}</button>
      </form>

      <table aria-label="incidents-table">
        <thead><tr><th>{t('m19.incident.colNumber')}</th><th>{t('m19.incident.colTitle')}</th><th>{t('m19.incident.colSeverity')}</th><th>{t('m19.incident.colStatus')}</th><th>{t('m19.incident.colOwner')}</th><th>{t('m19.incident.colActions')}</th></tr></thead>
        <tbody>
          {rows.map((i) => (
            <tr key={i.incidentId}>
              <td>{i.incidentNumber}</td>
              <td>{i.title}</td>
              <td>{i.severity}</td>
              <td>{i.status}</td>
              <td>{i.ownerId ?? t('m19.incident.unassigned')}</td>
              <td>
                <button type="button" aria-label={`resolve-${i.incidentId}`} disabled={busyId === i.incidentId + 'resolve'} onClick={() => void act(i.incidentId, 'resolve')}>{t('m19.incident.resolve')}</button>
                <button type="button" aria-label={`escalate-${i.incidentId}`} disabled={busyId === i.incidentId + 'escalate'} onClick={() => void act(i.incidentId, 'escalate')}>{t('m19.incident.escalate')}</button>
                <button type="button" aria-label={`close-${i.incidentId}`} disabled={busyId === i.incidentId + 'close'} onClick={() => void act(i.incidentId, 'close')}>{t('m19.incident.close')}</button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </main>
  );
}
